import { useState } from 'react'
import { AxiosRequestConfig } from 'axios'
import { trackInternalEvent } from '@rio/tracking'

import { useI18n } from 'builder/hooks/useI18n'
import { MouseClickEvent } from 'builder/modules/ui'
import { AutoApplyForm } from 'builder/modules/autoApply/types'
import { FileInput } from 'builder/components/PhotoUploadField/styles'

import { useUploadResume } from '../../../hooks/useUploadResume'
import { ALLOWED_FILES } from '../../../constants'
import UploadResumeImg from '../../../assets/UploadResume.png'

import { TRANSLATION } from '../constants'
import * as Styles from '../styles'
import UploadImgSrc from '../../../assets/upload.png'
import { Item } from '../Item/Item'
import { Progress, ProgressBar, ProgressContainer, ProgressLabel } from './styles'

type Props = {
  onResumeSuccess: (data: AutoApplyForm) => void
  IsTopResumeOrIsTopCV?: boolean
}

export const ResumeUpload = ({ onResumeSuccess, IsTopResumeOrIsTopCV }: Props) => {
  const { i18n } = useI18n()
  const [percentage, setPercentage] = useState(20)

  const onUploadProgress: AxiosRequestConfig['onUploadProgress'] = event => {
    const { loaded, total } = event
    setPercentage(Math.floor((loaded * 100) / total))
  }

  const {
    mutateAsync: uploadResume,
    isLoading,
    isError,
    error,
  } = useUploadResume(onUploadProgress, onResumeSuccess)

  const showErrorMessage = () => {
    const err = error?.response
    const resumeError = err?.data?.error?.resume
    const hasError = isError && err?.status === 422

    if (hasError && resumeError?.includes('too_large')) {
      return i18n.t(`${TRANSLATION}.upload_error.exceed_limit`)
    } else if (hasError && resumeError?.includes('invalid_file_type')) {
      return i18n.t(`${TRANSLATION}.upload_error.invalid_file_fromat`)
    } else {
      return i18n.t(`${TRANSLATION}.upload_error.generic`)
    }
  }

  const handleUploadClick = (event: MouseClickEvent) => {
    event.stopPropagation()
    trackInternalEvent('submit_resume', {
      label: 'total_job_search_solution',
      type: 'upload',
    })
  }

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedResume = event.target.files?.[0]
    if (selectedResume) {
      await uploadResume(selectedResume)
    }
  }

  if (isLoading) {
    return (
      <Styles.Box $isError={isError}>
        <ProgressContainer>
          <ProgressLabel>{i18n.t(`${TRANSLATION}.uploading`)}</ProgressLabel>
          <Progress>
            <ProgressBar progress={percentage} />
          </Progress>
        </ProgressContainer>
      </Styles.Box>
    )
  }

  const Body = (
    <>
      <Styles.UploadFromDevice htmlFor="auto-apply-file-resume-upload">
        {IsTopResumeOrIsTopCV ? (
          <Item
            showBorder
            icon={<img src={UploadImgSrc} />}
            title={i18n.t(`${TRANSLATION}.upload_resume_button.title`)}
            subtitle={i18n.t(`${TRANSLATION}.upload_resume_button.subtitle`)}
          />
        ) : (
          <>
            <Styles.ImageContainer>
              <Styles.Image $image={UploadResumeImg} />
            </Styles.ImageContainer>
            <Styles.TextContainer>
              <Styles.Header>{i18n.t(`${TRANSLATION}.upload_resume`)}</Styles.Header>
              <Styles.Description>{i18n.t(`${TRANSLATION}.existing_resume`)}</Styles.Description>
            </Styles.TextContainer>
          </>
        )}
        <FileInput
          type="file"
          onClick={handleUploadClick}
          onChange={handleFileInputChange}
          accept={ALLOWED_FILES}
          id="auto-apply-file-resume-upload"
        />
      </Styles.UploadFromDevice>
      {isError && (
        <Styles.ErrorContainer>
          <Styles.ErrorLabel>{showErrorMessage()}</Styles.ErrorLabel>
        </Styles.ErrorContainer>
      )}
    </>
  )

  if (IsTopResumeOrIsTopCV) {
    return Body
  }

  return <Styles.Box $isError={isError}>{Body}</Styles.Box>
}
